import { ViewMode } from '@playbooks/action-cards-utils'
import Head from './Head'
import Footer from './Footer'
import { useAppContext } from '../contexts/AppContext'
import getSectionsSet from '../utils/getSectionsSet'

import useActionCardsApiRegister, { ActionCardsElement } from '../hooks/useActionCardsApiRegister'
import useSlideWidthAdjustment from '../hooks/useSlideWidthAdjustment'
import useAccentColorUpdate from '../hooks/useAccentColorUpdate'
import NoSectionsAlert from './NoSectionsAlert'
import ActionCardsSections from './ActionCardsSections'
import useMountEffect from '../hooks/useMountEffect'
import tryToPlayFirstVideo from '../utils/tryToPlayFirstVideo'

interface ActionCardsProps {
  ref?: React.Ref<ActionCardsElement | null>
}

export default function ActionCards({ ref }: ActionCardsProps) {
  const { data, rootRef, settings } = useAppContext()

  const { scopes, visiblePinnedSections, slides } = getSectionsSet({
    actionCards: data,
    headersCount: settings?.headers,
    hideSeparatorsAbility: settings?.hideSeparatorsAbility ?? true,
    viewMode: settings?.viewMode,
    isSplitViewEnabled: settings.isSplitViewEnabled,
  })

  const isFunnelView = settings.viewMode === ViewMode.FUNNEL

  // Build a CSS expression for dynamic total width of the funnel view,
  // (horizontally scrolled), based on the number of slides that will be displayed.
  // Explicit width is required for sticky positioning of e.g. Teammate indicator.
  // Calculate the total number of slides for the funnel view, including an extra slide if applicable.
  const funnelSlidesCount = Math.max(scopes.length + (settings.isWebplay && !!settings.lastSlideHtml ? 1 : 0), 1)
  const funnelWidth = `calc(${Array.from({ length: funnelSlidesCount }, () => 'var(--slide-width)').join(' + ')})`

  useActionCardsApiRegister(ref, slides)
  useAccentColorUpdate()
  useSlideWidthAdjustment()

  useMountEffect(() => {
    if (!rootRef.current || !settings.isWebplay || settings?.viewMode === ViewMode.FUNNEL) {
      return
    }
    tryToPlayFirstVideo(rootRef.current)
  })

  return (
    <div
      ref={rootRef}
      data-view={settings.viewMode || ViewMode.DEFAULT}
      data-role="action-cards-app"
      className="relative group/app flex flex-col h-full w-full bg-background text-foreground data-[view=funnel]:shrink-0"
      style={isFunnelView ? { width: funnelWidth } : undefined}
    >
      <Head />

      {data.sections.length === 0 ? (
        <NoSectionsAlert />
      ) : (
        <ActionCardsSections
          hideScore={!!settings.hideScore}
          splitViewPosition={settings.splitViewPosition || 'left'}
          viewMode={settings.viewMode}
          isSplitViewEnabled={settings.isSplitViewEnabled}
          visiblePinnedSections={visiblePinnedSections}
          scopes={scopes}
          lastSlideHtml={settings.lastSlideHtml}
        />
      )}

      <Footer />
    </div>
  )
}
